import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { detectPlatform, embedSrc, isVertical, tiktokId } from "@/lib/video";

function loadScript(src) {
  return new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { resolve(existing); return; }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve(s);
    document.body.appendChild(s);
  });
}

// Official TikTok / Instagram embeds — these auto-size to the real player
// (video + caption + audio bar), so nothing gets clipped by a fixed frame.
export function SocialEmbed({ platform, url, testid }) {
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (platform === "tiktok") {
      // Re-inject so embed.js re-scans and renders this blockquote.
      const prev = document.querySelector('script[src="https://www.tiktok.com/embed.js"]');
      if (prev) prev.remove();
      loadScript("https://www.tiktok.com/embed.js");
    } else if (platform === "instagram") {
      loadScript("https://www.instagram.com/embed.js").then(() => {
        if (!cancelled && window.instgrm?.Embeds) window.instgrm.Embeds.process();
      });
    } else if (platform === "facebook") {
      loadScript("https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v19.0").then(() => {
        setTimeout(() => { if (!cancelled && window.FB?.XFBML) window.FB.XFBML.parse(); }, 300);
      });
    }
    return () => { cancelled = true; };
  }, [platform, url]);

  if (platform === "tiktok") {
    return (
      <blockquote
        ref={ref}
        className="tiktok-embed"
        cite={url}
        data-video-id={tiktokId(url)}
        data-testid={testid}
        style={{ maxWidth: 605, minWidth: 260, margin: "0 auto", width: "100%" }}
      >
        <section></section>
      </blockquote>
    );
  }
  if (platform === "facebook") {
    const isVideo = /\/videos\/|\/watch|fb\.watch|\/reel\//i.test(url);
    return (
      <div data-testid={testid} style={{ width: "100%", maxWidth: 540, margin: "0 auto" }}>
        <div id="fb-root"></div>
        <div ref={ref} className={isVideo ? "fb-video" : "fb-post"} data-href={url} data-width="500" data-lazy="true"></div>
      </div>
    );
  }
  return (
    <blockquote
      ref={ref}
      className="instagram-media"
      data-instgrm-permalink={url}
      data-instgrm-version="14"
      data-testid={testid}
      style={{ maxWidth: 540, minWidth: 260, margin: "0 auto", width: "100%", background: "#fff" }}
    ></blockquote>
  );
}

// Renders raw pasted embed code (iframe/blockquote) and executes any embed scripts.
export function RawEmbed({ html, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = html || "";
    el.querySelectorAll("script").forEach((old) => {
      const s = document.createElement("script");
      for (const a of Array.from(old.attributes)) s.setAttribute(a.name, a.value);
      s.text = old.text || "";
      old.parentNode.replaceChild(s, old);
    });
    const t = setTimeout(() => {
      try { window.instgrm?.Embeds?.process?.(); } catch (e) { /* noop */ }
      try { window.FB?.XFBML?.parse?.(); } catch (e) { /* noop */ }
    }, 500);
    return () => clearTimeout(t);
  }, [html]);
  return <div ref={ref} className={className} data-testid="raw-embed" />;
}

// Renders a playable embed for any supported platform.
export function VideoEmbed({ platform, url, className = "", testid }) {
  const p = platform || detectPlatform(url);
  if (!url) return null;
  if (p === "file") {
    return (
      <video src={url} controls playsInline className={className} data-testid={testid}>
        Your browser does not support video.
      </video>
    );
  }
  if (p === "tiktok" || p === "instagram" || p === "facebook") {
    return (
      <div className={className} data-testid={testid}>
        <SocialEmbed platform={p} url={url} />
      </div>
    );
  }
  return (
    <iframe
      src={embedSrc(p, url)}
      className={className}
      data-testid={testid}
      title="Video"
      loading="lazy"
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  );
}

// A lazy poster+play card for iframe platforms (YouTube/Facebook) and hosted files.
export function VideoCard({ video, className = "" }) {
  const [play, setPlay] = useState(false);
  const p = video.platform || detectPlatform(video.url);
  const ratio = isVertical(p) ? "aspect-[9/16]" : "aspect-video";
  if (play || !video.thumbnail) {
    return (
      <div className={`relative w-full ${ratio} bg-black ${className}`}>
        <VideoEmbed platform={p} url={video.url} className="absolute inset-0 h-full w-full" />
      </div>
    );
  }
  return (
    <button onClick={() => setPlay(true)} data-testid={`video-play-${video.id}`}
      className={`group relative w-full ${ratio} overflow-hidden bg-black ${className}`}>
      <img src={video.thumbnail} alt={video.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/10">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-primary shadow-lg transition-transform group-hover:scale-110">
          <Play size={24} className="ml-0.5 fill-current" />
        </span>
      </span>
    </button>
  );
}

// Full-bleed muted looping background video for the homepage hero.
export function HeroVideo({ url, className = "" }) {
  const p = detectPlatform(url);
  if (!url) return null;
  if (p === "file") {
    return (
      <video src={url} autoPlay muted loop playsInline className={className} data-testid="hero-video" />
    );
  }
  return (
    <iframe
      src={embedSrc(p, url, { autoplay: true })}
      className={className}
      data-testid="hero-video"
      title="Hero video"
      frameBorder="0"
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
    />
  );
}
